import service from '../services/search_history'
import {
  historyKey,
  dedupeNewestFirst,
  upsert,
  removeByKey,
  readCache,
  writeCache,
  clearCache
} from '~/helpers/search-history'

const currentUserId = (rootState) => rootState.auth?.user?.id ?? null

export const state = {
  items: [],
  // Guards hydrate so the server GET runs once per session (reset on login/logout).
  hydrated: false
}

export const getters = {
  recentSearches: (state) => state.items
}

export const mutations = {
  SET_ITEMS (state, items) {
    state.items = items
  },
  SET_HYDRATED (state, value) {
    state.hydrated = value
  }
}

export const actions = {
  /**
   * Instant: seed from the per-user localStorage cache. Background: reconcile with the
   * server. The server is AUTHORITATIVE for what still exists, so a row deleted on another
   * device (absent from the GET) drops out of this device's cache too — a plain local-first
   * union could only ever add rows, never honour a remote delete. To still protect an
   * optimistic add made while this GET was in flight, we re-apply only the items that appeared
   * locally AFTER we seeded (i.e. not in the seeded snapshot) on top of the server list.
   */
  hydrate ({ commit, state, rootState }, { force = false } = {}) {
    if (state.hydrated && !force) return
    commit('SET_HYDRATED', true)

    const userId = currentUserId(rootState)
    const seeded = dedupeNewestFirst(readCache(userId))
    commit('SET_ITEMS', seeded)
    const seededKeys = new Set(seeded.map(historyKey))

    service.list()
      .then(({ data }) => {
        const server = (data && data.results) || []
        // Only genuine in-flight adds (not present when we seeded) survive; seeded rows that
        // the server no longer has were deleted elsewhere and must not be resurrected.
        const optimisticAdds = state.items.filter((item) => !seededKeys.has(historyKey(item)))
        const merged = dedupeNewestFirst([...optimisticAdds, ...server])
        commit('SET_ITEMS', merged)
        writeCache(userId, merged)
      })
      .catch(() => {
        // Server unreachable — keep the local cache; next hydrate reconciles.
      })
  },

  add ({ commit, state, rootState }, item) {
    const withKey = { ...item, dedup_key: historyKey(item) }
    const items = upsert(state.items, withKey)
    commit('SET_ITEMS', items)
    writeCache(currentUserId(rootState), items)
    service.add(withKey).catch(() => {}) // fire-and-forget; reconciles on next hydrate
  },

  remove ({ commit, state, rootState }, key) {
    const items = removeByKey(state.items, key)
    commit('SET_ITEMS', items)
    writeCache(currentUserId(rootState), items)
    service.remove(key).catch(() => {})
  },

  clearAll ({ commit, rootState }) {
    commit('SET_ITEMS', [])
    writeCache(currentUserId(rootState), [])
    service.clear().catch(() => {})
  },

  // Logout: drop the in-memory list + this user's cache (the LOGOUT mutation only resets
  // user/token, so the module must clear itself). Server history is untouched.
  clearLocal ({ commit, rootState }) {
    clearCache(currentUserId(rootState))
    commit('SET_ITEMS', [])
    commit('SET_HYDRATED', false)
  }
}
